import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { api } from '../lib/api';

type BlogCard = {
  slug: string;
  title: string;
  excerpt: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  category: string | null;
  publishedAt: string | null;
};

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Landing teaser for the blog. Desktop shows a bento layout — one big featured
 * post (image with text overlaid), two stacked overlay cards beside it, and a
 * bottom row of three light cards. On mobile everything stacks into a single
 * column. Self-contained (fetches its own data); renders nothing until there
 * are posts. Shares the ['blog','index'] cache with the /blog page.
 */
export function BlogTeaser() {
  const { data } = useQuery({
    queryKey: ['blog', 'index'],
    queryFn: () => api<{ posts: BlogCard[] }>('/api/blog'),
  });
  const posts = (data?.posts ?? []).slice(0, 6);
  if (posts.length === 0) return null;

  const [hero, ...rest] = posts;
  const sideCards = rest.slice(0, 2); // stacked to the right of the hero
  const bottomCards = rest.slice(2, 5); // bottom row

  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Del blog</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
            Aprende a sacarle<br />
            partido a tus redes.
          </h2>
        </div>

        {/* Top block: hero + two stacked overlay cards */}
        <div className="mt-12 grid gap-5 lg:grid-cols-3 lg:grid-rows-2">
          {hero && <HeroCard post={hero} />}
          {sideCards.map((p) => (
            <OverlayCard key={p.slug} post={p} className="min-h-[190px] lg:min-h-0" />
          ))}
        </div>

        {/* Bottom row: three light cards */}
        {bottomCards.length > 0 && (
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {bottomCards.map((p) => (
              <PlainCard key={p.slug} post={p} />
            ))}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 font-semibold text-slate-700 hover:border-brand-300 hover:text-brand-700 hover:bg-brand-50 transition group"
          >
            Ver todos los artículos
            <ArrowRight size={18} className="transition group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Featured post — large card, image to the edges, text over a dark scrim. */
function HeroCard({ post }: { post: BlogCard }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group relative overflow-hidden rounded-3xl bg-slate-800 lg:col-span-2 lg:row-span-2 min-h-[340px] lg:min-h-[440px]"
    >
      {post.featuredImageUrl && (
        <img
          src={post.featuredImageUrl}
          alt={post.featuredImageAlt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-slate-950/5" />
      <div className="relative flex h-full flex-col justify-end p-6 lg:p-8 text-white">
        {post.category && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-brand-200">
            {post.category}
          </span>
        )}
        <h3 className="mt-2 text-2xl lg:text-3xl font-bold leading-tight line-clamp-3">
          {post.title}
        </h3>
        <p className="mt-2.5 text-sm lg:text-base text-white/75 leading-relaxed line-clamp-2 max-w-xl">
          {post.excerpt}
        </p>
        <span className="mt-4 text-xs text-white/60">{fmtDate(post.publishedAt)}</span>
      </div>
    </Link>
  );
}

/** Small image-forward card with the title overlaid — used beside the hero. */
function OverlayCard({ post, className = '' }: { post: BlogCard; className?: string }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group relative overflow-hidden rounded-2xl bg-slate-800 ${className}`}
    >
      {post.featuredImageUrl && (
        <img
          src={post.featuredImageUrl}
          alt={post.featuredImageAlt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
      <div className="relative flex h-full flex-col justify-end p-5 text-white">
        {post.category && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-200">
            {post.category}
          </span>
        )}
        <h3 className="mt-1.5 text-base lg:text-lg font-bold leading-snug line-clamp-2">
          {post.title}
        </h3>
      </div>
    </Link>
  );
}

/** Standard light card — image on top, text below. Used in the bottom row. */
function PlainCard({ post }: { post: BlogCard }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-brand-300 hover:shadow-lg"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        {post.featuredImageUrl && (
          <img
            src={post.featuredImageUrl}
            alt={post.featuredImageAlt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        {post.category && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-600">
            {post.category}
          </span>
        )}
        <h3 className="mt-1 line-clamp-2 text-base font-bold leading-snug text-slate-900 transition group-hover:text-brand-700">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-500">{post.excerpt}</p>
        <p className="mt-3 text-xs text-slate-400">{fmtDate(post.publishedAt)}</p>
      </div>
    </Link>
  );
}
